/**
 * The `Capability` type — every real backend RBAC capability key, kept in
 * sync with `backend/database/seeds/create_rbac_defaults.py`'s
 * `PERMISSIONS` table by hand (no codegen exists yet). This is a TypeScript
 * type only, for `usePermission().can(...)`'s parameter — it carries no
 * runtime data of its own.
 *
 * Retired 2026-08-14 (AI_RECRUIT_DYNAMIC_RBAC_MASTER_PLAN.md §10.1): this
 * file used to also export a static `ROLE_PERMISSIONS` table
 * (role → capability[], derived from a hardcoded `MIN_ROLE` map) that
 * `usePermission()` read from directly. That table could never reflect a
 * custom role's real grants — only the 6 original roles existed in it —
 * so it's gone. `usePermission()` now reads live grants from
 * `GET /auth/me/capabilities` instead; this file only supplies the type.
 */

export type Capability =
  | 'jobs.create' | 'jobs.edit' | 'jobs.changeStatus' | 'jobs.delete' | 'jobs.reprocess'
  | 'jobs.manageRecruiters' | 'jobs.viewList' | 'jobs.viewDetails' | 'jobs.downloadJD' | 'jobs.applyResume'
  | 'resumes.upload' | 'resumes.reprocess' | 'resumes.delete' | 'resumes.blacklist'
  | 'resumes.viewList' | 'resumes.viewDetails' | 'resumes.downloadOriginal' | 'resumes.downloadFormatted'
  | 'interviews.view' | 'interviews.create' | 'interviews.generate' | 'interviews.forceStatus' | 'interviews.delete'
  | 'interviewGuides.generate'
  | 'users.create' | 'users.edit' | 'users.delete' | 'users.view'
  | 'users.resetPasswordDefault' | 'users.setCustomPassword'
  | 'users.viewAll' | 'users.viewRoster' | 'users.createManager'
  | 'teams.viewOwn' | 'teams.addMemberOwn' | 'teams.removeMemberOwn'
  | 'teams.create' | 'teams.rename' | 'teams.reassignManager' | 'teams.delete'
  | 'sources.createJobSource' | 'sources.editJobSource' | 'sources.activateDeactivateJobSource'
  | 'sources.createResumeSource' | 'sources.editResumeSource' | 'sources.activateDeactivateResumeSource'
  | 'sources.viewJobSources' | 'sources.viewResumeSources'
  | 'assignments.manageResumes' | 'assignments.manageJobMatches' | 'assignments.viewAuditHistory'
  | 'analytics.viewCostDashboard' | 'analytics.teamDashboard'
  | 'candidateStatus.change'
  | 'dashboard.view' | 'dashboard.export'
  | 'matching.view' | 'matching.newMatch'
  | 'roles.viewList' | 'roles.create' | 'roles.edit' | 'roles.delete'
  | 'roles.viewAuditLog'
  // Live Interview (Task 15/18) — gated via `gated("liveInterview.manage"/
  // "liveInterview.view", ...)` in backend/api/v2/endpoints/live_interview.py.
  // Own "Live Interview" module (split out from "Interviews" 2026-10-07, see
  // create_rbac_defaults.py) so it renders as its own section in Roles &
  // Permissions, independent of the plain AI Interview keys above.
  | 'liveInterview.manage' | 'liveInterview.view'
  // Proctored Interview — gated via `gated("proctoredInterviews.manage"/
  // "proctoredInterviews.view", ...)` in backend/api/v2/endpoints/
  // proctored_interviews.py. Added to the catalog 2026-10-07 (own
  // "Proctored Interview" module) — previously missing entirely, so the
  // frontend tab had to proxy its visibility off the AI Interview keys
  // (see JobDetailPage.tsx); that proxy is removed in the same pass.
  | 'proctoredInterviews.manage' | 'proctoredInterviews.view'
  // Complexity Levels (backend/api/v2/endpoints/complexity_levels.py) — own
  // "Complexity Levels" module; .view also feeds the question-mix picker.
  | 'complexityLevels.view' | 'complexityLevels.create' | 'complexityLevels.edit' | 'complexityLevels.delete'
  // Reports (backend/api/v2/endpoints/reports.py) — Organization Settings > Reports tab.
  | 'reports.viewDownload'
  // AI Template Generator resume formatter (backend/api/v2/endpoints/resume_templates.py) —
  // gated via `gated("resumeTemplates.view"/"resumeTemplates.manage", ...)`.
  | 'resumeTemplates.view' | 'resumeTemplates.manage'

// The set of capabilities Organization Settings' own tabs are gated on
// (OrganizationSettingsPage.tsx's navItems, §23) — used to decide whether
// the /org-settings route and its "Organization Settings" nav link should
// be reachable at all. Deliberately NOT `users.viewAll`/`users.viewRoster`/
// `users.createManager` — those gate actions elsewhere, not any tab's
// visibility here; including them would let a role reach the page with
// every tab still hidden; the same bug in reverse. `orgSettings.access`
// itself was removed from the capability catalog entirely on 2026-08-16
// (§25) — it had zero backend enforcement and no purpose independent of
// these six, so this constant (not a standalone capability) is now the
// single source of truth for "can this role see anything on this page."
export const ORG_SETTINGS_CAPABILITIES: Capability[] = [
  'teams.viewOwn',
  'teams.create',
  'teams.rename',
  'teams.reassignManager',
  'teams.delete',
  'sources.createJobSource',
  'sources.editJobSource',
  'sources.activateDeactivateJobSource',
  'sources.createResumeSource',
  'sources.editResumeSource',
  'sources.activateDeactivateResumeSource',
  'sources.viewJobSources',
  'sources.viewResumeSources',
  'users.create',
  'users.edit',
  'users.delete',
  'users.resetPasswordDefault',
  'users.setCustomPassword',
  'users.view',
  'analytics.viewCostDashboard',
  // roles.manage retired 2026-08-31 -> six ordinary keys; roles.viewList is
  // the minimum-visibility signal for the Roles tab, same as users.view
  // above for the Users tab (not users.create/edit/delete individually).
  'roles.viewList',
  'resumeTemplates.view',
  'complexityLevels.view',
  'reports.viewDownload',
  'resumeTemplates.manage',
]
